import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { FocusEvent, useCallback, useEffect, useState } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { useGateWithExposureLoggingDisabled } from "statsig-react";
import { BrandInputPartial } from "../../../mutations/__generated__/useBrandUpdateMutation.graphql";
import { IntegrationsTabQuery } from "./__generated__/IntegrationsTabQuery.graphql";
import {
  IntegrationsTab_brand$data,
  IntegrationsTab_brand$key,
} from "./__generated__/IntegrationsTab_brand.graphql";
import CardSection from "../../../components/CardSection";
import ConfirmDialog from "../../../components/ConfirmDialog";
import CopyButton from "../../../components/CopyButton";
import RighthandDrawer from "../../../components/RighthandDrawer";
import SaveButton, { SavedState } from "../../../components/SaveButton";
import useBrandUpdateMutation from "../../../mutations/useBrandUpdateMutation";
import {
  SAVED_SECRET_MASK,
  SAVED_SECRET_PLACEHOLDER,
} from "../../../utils/constants";
import CookiesCard from "./integrations/CookiesCard";
import DiscourseCard from "./integrations/DiscourseCard";
import SlackCard from "./integrations/SlackCard";
import SocialLoginCard from "./integrations/SocialLoginCard";

type IntegrationField = {
  label: string;
  // Readable fields expose their value; write-only secrets only whether one is saved.
  value?: (brand: IntegrationsTab_brand$data) => string;
  isSecretSet?: (brand: IntegrationsTab_brand$data) => boolean;
  saveValue?: (
    brand: IntegrationsTab_brand$data,
    value: string
  ) => BrandInputPartial;
};

type IntegrationFieldGroup = {
  label?: string;
  isReadonly?: boolean;
  fields: IntegrationField[];
};

const isWriteOnly = (field: IntegrationField) =>
  field.isSecretSet !== undefined;

const getDisplayValue = (
  field: IntegrationField,
  brand: IntegrationsTab_brand$data
) =>
  isWriteOnly(field)
    ? field.isSecretSet!(brand)
      ? SAVED_SECRET_MASK
      : ""
    : field.value?.(brand) ?? "";

const API_KEY_FIELD_GROUPS: IntegrationFieldGroup[] = [
  {
    fields: [
      {
        label: "Gorgias Brand ID",
        value: (brand) => brand.gorgiasId ?? "",
        saveValue: (brand, value) => ({ gorgiasId: value }),
      },
    ],
  },
  {
    isReadonly: true,
    fields: [
      {
        label: "Kinn API Key",
        value: (brand) => brand.kinnApiToken ?? "",
      },
    ],
  },
  {
    fields: [
      {
        label: "Klaviyo API Key",
        isSecretSet: (brand) => brand.hasKlaviyoApiToken,
        saveValue: (brand, value) => ({ klaviyoApiToken: value }),
      },
    ],
  },
  {
    fields: [
      {
        label: "Kustomer API Key",
        isSecretSet: (brand) => brand.hasKustomerApiToken,
        saveValue: (brand, value) => ({ kustomerApiToken: value }),
      },
    ],
  },
  {
    fields: [
      {
        label: "Ometria API Key",
        isSecretSet: (brand) => brand.hasOmetriaApiToken,
        saveValue: (brand, value) => ({ ometriaApiToken: value }),
      },
    ],
  },
  {
    fields: [
      {
        label: "Recharge API Key",
        isSecretSet: (brand) => brand.hasRechargeApiToken,
        saveValue: (brand, value) => ({ rechargeApiToken: value }),
      },
    ],
  },
  {
    label: "Emarsys Credentials",
    fields: [
      {
        label: "Client ID",
        value: (brand) => brand.emarsysApiClientId ?? "",
        saveValue: (brand, value) => ({ emarsysApiClientId: value }),
      },
      {
        label: "Emarsys API Client Secret",
        isSecretSet: (brand) => brand.hasEmarsysApiClientSecret,
        saveValue: (brand, value) => ({ emarsysApiClientSecret: value }),
      },
    ],
  },
  {
    fields: [
      {
        label: "Appsflyer Dev Key",
        value: (brand) => brand.appsflyerDevKey ?? "",
        saveValue: (brand, value) => ({ appsflyerDevKey: value }),
      },
    ],
  },
];

const query = graphql`
  query IntegrationsTabQuery($id: ID!) {
    brand(id: $id) {
      ...IntegrationsTab_brand
    }
  }
`;

const brandFragment = graphql`
  fragment IntegrationsTab_brand on BrandType {
    ...useBrandUpdateMutation_brand
    ...CookiesCard_brand
    ...DiscourseCard_brand
    ...SlackCard_brand
    ...SocialLoginCard_brand
    gorgiasId
    hasKlaviyoApiToken
    kinnApiToken
    hasKustomerApiToken
    hasOmetriaApiToken
    emarsysApiClientId
    hasEmarsysApiClientSecret
    hasRechargeApiToken
    appsflyerDevKey
  }
`;

type Props = {
  brandID: string;
};

const IntegrationsTabDrawer = ({
  brand,
  fieldGroup,
  isOpen,
  onClose,
}: {
  brand: IntegrationsTab_brand$data;
  fieldGroup: IntegrationFieldGroup | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const groupName = fieldGroup
    ? fieldGroup.label ?? fieldGroup.fields[0].label
    : "";
  const label = fieldGroup ? "Edit " + groupName : "";
  const [updateBrand, isBrandMutationInFlight] = useBrandUpdateMutation(brand);

  const [saveButtonState, setSaveButtonState] = useState<SavedState>(
    SavedState.DISABLED
  );
  const [textFieldValues, setTextFieldValues] = useState<string[]>([]);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);

  const onCloseDrawer = useCallback(() => {
    onClose();
    setTextFieldValues([]);
    setSaveButtonState(SavedState.DISABLED);
    setIsRemoveDialogOpen(false);
  }, [onClose, setTextFieldValues, setSaveButtonState]);

  useEffect(() => {
    setTextFieldValues(
      fieldGroup
        ? fieldGroup.fields.map((field) =>
            isWriteOnly(field) ? "" : field.value?.(brand) ?? ""
          )
        : []
    );
  }, [fieldGroup, brand]);

  return (
    <RighthandDrawer isOpen={isOpen} title={label} onClose={onCloseDrawer}>
      <Stack
        direction="column"
        sx={{
          padding: 2,
        }}
        spacing={2}
        alignItems={"flex-end"}
      >
        {fieldGroup?.fields.map((field, index) => (
          <TextField
            key={index}
            fullWidth
            margin="normal"
            label={field.label}
            variant="outlined"
            value={textFieldValues[index] ?? ""}
            placeholder={
              field.isSecretSet?.(brand) ? SAVED_SECRET_PLACEHOLDER : undefined
            }
            InputLabelProps={
              field.isSecretSet?.(brand) ? { shrink: true } : undefined
            }
            onChange={(event: FocusEvent<HTMLInputElement>) => {
              setTextFieldValues((prev) => {
                const newValues = [...prev];
                newValues[index] = event.target.value;
                return newValues;
              });
              saveButtonState !== SavedState.ENABLED &&
                setSaveButtonState(SavedState.ENABLED);
            }}
          />
        ))}
        {fieldGroup !== null &&
        fieldGroup.fields.every((field) => field.saveValue) &&
        fieldGroup.fields.some((field) => field.isSecretSet?.(brand)) ? (
          <>
            <Button
              color="error"
              disabled={isBrandMutationInFlight}
              onClick={() => setIsRemoveDialogOpen(true)}
            >
              Remove
            </Button>
            <ConfirmDialog
              isOpen={isRemoveDialogOpen}
              title={`Remove ${groupName}?`}
              body={`The saved ${groupName} will be deleted and the integration will stop working until new credentials are saved.`}
              confirmLabel="Remove"
              isConfirmDisabled={isBrandMutationInFlight}
              onClose={() => setIsRemoveDialogOpen(false)}
              onConfirm={() => {
                const inputPartial = fieldGroup.fields.reduce(
                  (acc, field) => ({ ...acc, ...field.saveValue!(brand, "") }),
                  {} as BrandInputPartial
                );
                updateBrand(inputPartial, (_) => onCloseDrawer());
              }}
            />
          </>
        ) : null}
        <SaveButton
          savedState={
            isBrandMutationInFlight ? SavedState.DISABLED : saveButtonState
          }
          onClick={
            fieldGroup === null
              ? undefined
              : () => {
                  const saveValues = fieldGroup.fields
                    .map((field, index) =>
                      // An empty write-only field keeps the saved secret.
                      isWriteOnly(field) && !textFieldValues[index]
                        ? undefined
                        : field.saveValue?.(brand, textFieldValues[index] ?? "")
                    )
                    .filter((value) => value !== undefined);
                  const inputPartial = saveValues.reduce(
                    (acc, value) => ({ ...acc, ...value }),
                    {}
                  );
                  updateBrand(inputPartial!, (_) => {
                    setSaveButtonState(SavedState.SAVED);
                    onCloseDrawer();
                  });
                }
          }
        />
      </Stack>
    </RighthandDrawer>
  );
};

const IntegrationFieldGroupList = ({
  brand,
  fieldGroups,
  onEditClick,
}: {
  brand: IntegrationsTab_brand$data;
  fieldGroups: IntegrationFieldGroup[];
  onEditClick: (field: IntegrationFieldGroup) => void;
}) => {
  return (
    <Stack>
      {fieldGroups.map((field, index) => {
        const value = field.fields
          .map((field) => getDisplayValue(field, brand))
          .join("");
        const isEmpty = value.length === 0;
        const hasWriteOnlyField = field.fields.some(isWriteOnly);
        const actions = (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            {hasWriteOnlyField ? null : (
              <CopyButton
                textToCopy={value}
                isDisabled={isEmpty || field.fields.length > 1}
              />
            )}
            {field.fields.some((field) => field.saveValue) ? (
              <Button
                startIcon={<EditIcon />}
                onClick={() => onEditClick(field)}
              >
                Edit
              </Button>
            ) : null}
          </Stack>
        );
        return (
          <Stack
            key={index}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: "100%" }}
          >
            <TextField
              fullWidth
              disabled={isEmpty}
              margin="normal"
              id="outlined-basic"
              label={field.label ?? field.fields[0].label}
              variant="outlined"
              value={"•".repeat(value.length)}
              sx={{ flexGrow: 1, minWidth: 0 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ display: { xs: "none", sm: "flex" } }}
                  >
                    {actions}
                  </InputAdornment>
                ),
                readOnly: true,
              }}
            />
            <Box sx={{ display: { xs: "block", sm: "none" } }}>{actions}</Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

const IntegrationsTab = ({ brandID }: Props) => {
  const data = useLazyLoadQuery<IntegrationsTabQuery>(query, {
    id: brandID,
  });

  const brand = useFragment<IntegrationsTab_brand$key>(
    brandFragment,
    data.brand
  );

  const [drawerField, setDrawerField] = useState<IntegrationFieldGroup | null>(
    null
  );
  const isDrawerOpen = drawerField !== null;
  const onDrawerClose = useCallback(() => {
    setDrawerField(null);
  }, [setDrawerField]);
  const onEditClick = useCallback(
    (field: IntegrationFieldGroup) => {
      setDrawerField(field);
    },
    [setDrawerField]
  );

  const { value: isBrandEligibleForDiscourse } =
    useGateWithExposureLoggingDisabled("discourse_sso_eligible_brands");

  return (
    <Container>
      <CardSection
        title={"API Keys"}
        content={
          <IntegrationFieldGroupList
            brand={brand}
            fieldGroups={API_KEY_FIELD_GROUPS}
            onEditClick={onEditClick}
          />
        }
      />
      <SocialLoginCard brand={brand} />
      <SlackCard brand={brand} />
      {isBrandEligibleForDiscourse ? <DiscourseCard brand={brand} /> : null}
      <CookiesCard brand={brand} />
      <IntegrationsTabDrawer
        brand={brand}
        fieldGroup={drawerField}
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
      />
    </Container>
  );
};

export default IntegrationsTab;
